/**
 * TanStack Query hooks (Sprint 13).
 *
 * Tenant-aware: query key'lerde tenantId zorunlu (cache invalidation).
 */
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiClient } from "./client";

// ---------- Helper: tenant key ----------
function getTenantId(): string {
  try {
    const t = JSON.parse(localStorage.getItem("auth_tenant") || "null");
    return t?.id || "anon";
  } catch {
    return "anon";
  }
}

// ============================================================
// Auth / Me
// ============================================================
export function useMe() {
  return useQuery({
    queryKey: ["me", getTenantId()],
    queryFn: async () => (await apiClient.get("/api/auth/me/")).data,
  });
}

// ============================================================
// Catalog
// ============================================================
export function useCategories() {
  return useQuery({
    queryKey: ["categories", getTenantId()],
    queryFn: async () => (await apiClient.get("/api/v1/categories/")).data,
  });
}

// Kategoriler tablosunda sürükle-bırak sıralama.
export function useReorderCategories() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (order: string[]) =>
      (await apiClient.post(`/api/v1/categories/reorder/`, { order })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["categories"] });
    },
  });
}

// Tedarik kategorileri sürükle-bırak sıralama.
export function useReorderProcurementCategories() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (order: string[]) =>
      (await apiClient.post(`/api/v1/procurement-categories/reorder/`, { order })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["procurement-categories"] });
    },
  });
}

export function useProducts(filters?: Record<string, any>) {
  return useQuery({
    queryKey: ["products", getTenantId(), filters],
    queryFn: async () =>
      (await apiClient.get("/api/v1/products/", { params: filters })).data,
  });
}

// ---------- Ürün galeri görselleri (max 4: kapak + 3 ek) ----------
export function useProductImages(productId: string | null) {
  return useQuery({
    queryKey: ["product-images", getTenantId(), productId],
    queryFn: async () =>
      (await apiClient.get("/api/v1/product-images/", {
        params: { product: productId },
      })).data,
    enabled: !!productId,
  });
}

export function useUploadProductImage() {
  const qc = useQueryClient();
  return useMutation({
    // payload: FormData (product, image) — multipart
    mutationFn: async (formData: FormData) =>
      (await apiClient.post("/api/v1/product-images/", formData)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["product-images"] });
      qc.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

export function useDeleteProductImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await apiClient.delete(`/api/v1/product-images/${id}/`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["product-images"] });
      qc.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

// ============================================================
// POS / Orders
// ============================================================
export function useOrders(filters?: Record<string, any>) {
  return useQuery({
    queryKey: ["orders", getTenantId(), filters],
    queryFn: async () =>
      (await apiClient.get("/api/v1/orders/", { params: filters })).data,
  });
}

export function useTables(options?: { wsConnected?: boolean }) {
  // WS bağlıyken polling kapalı (canlılık push ile gelir); WS koparsa
  // 15 sn fallback polling. Çağıran sayfa WS status'unu geçer; geçmezse
  // güvenli varsayılan: polling açık.
  const wsOk = options?.wsConnected ?? false;
  return useQuery({
    queryKey: ["tables", getTenantId()],
    queryFn: async () => (await apiClient.get("/api/v1/tables/")).data,
    refetchInterval: wsOk ? false : 15000,
  });
}

// Masa detayı — salon planında masaya tıklanınca açılan panel
// (açık siparişler, garson, toplam, süre, bugün kaç kez açıldı).
//
// refetchInterval: KDS akışı sipariş statüsünü ilerlettiğinde (PREPARING →
// READY → SERVED) normalde WebSocket eventi POS'u tazeler. WS bağlantısı
// koparsa POS "Onaylandı" gibi eski durumda takılı kalıyordu — QR
// ekranında SERVED görünürken POS ONAYLANDI gösteriyordu. 10sn'lik fallback
// poll bu uyumsuzluğu kısa süre içinde kendiliğinden çözer. WS çalışırken
// background fetch çoğunlukla 304/no-op olur, ek yük getirmez.
export function useTableDetail(tableId: string | null) {
  return useQuery({
    queryKey: ["table-detail", getTenantId(), tableId],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/tables/${tableId}/detail/`)).data,
    enabled: !!tableId,
    refetchInterval: 10000,
  });
}

// ===========================================================================
// TableSession (Adisyon) hooks
// ===========================================================================
// Bir masada açık olan üst-entity. table_detail içinde session özeti gelir
// (POS/admin masa drawer'ı üstte "Adisyon #ID · N sipariş · ₺X" gösterir).
// Detay endpoint'i bağlı Order id'leri + Check'ler + ödeme tercihini döner.

export function useTableSession(sessionId: string | null) {
  return useQuery({
    queryKey: ["table-session", getTenantId(), sessionId],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/table-sessions/${sessionId}/`)).data,
    enabled: !!sessionId,
    refetchInterval: 10000,
  });
}

// LOCK — "Hesap iste/al" başlangıcı. OPEN → LOCKED. Yeni Order alınamaz.
// Garson Hesap Al modal'ını açtığında veya QR müşterisi hesap istediğinde
// çağrılır. split_mode/parts opsiyonel.
export function useLockSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      sessionId: string;
      split_mode?: "single" | "split";
      split_parts?: number;
    }) => {
      const { sessionId, ...body } = vars;
      const { data } = await apiClient.post(
        `/api/v1/table-sessions/${sessionId}/lock/`,
        body,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["table-session"] });
      qc.invalidateQueries({ queryKey: ["table-detail"] });
      qc.invalidateQueries({ queryKey: ["tables"] });
    },
  });
}

// TENDER — Session'daki tüm açık Order'ları tek istekle kapat.
// Backend: payment.services.tender_session — Check'ler oluşur, Payment'lar
// onlara bağlanır, Session.status = CLOSED. Otomatik ÖKC fiş (Adım 5)
// payment.completed eventine bağlı olarak tetiklenir.
export function useTenderSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      sessionId: string;
      tenders: Array<{
        type: string;
        amount_minor: number;
        received_minor?: number;
        provider_reference?: string;
      }>;
      split_mode?: "single" | "split";
      split_parts?: number;
      idempotency_key?: string;
    }) => {
      const { sessionId, idempotency_key, ...body } = vars;
      const headers: Record<string, string> = {};
      if (idempotency_key) headers["Idempotency-Key"] = idempotency_key;
      const { data } = await apiClient.post(
        `/api/v1/table-sessions/${sessionId}/tender/`,
        body,
        { headers },
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["table-session"] });
      qc.invalidateQueries({ queryKey: ["table-detail"] });
      qc.invalidateQueries({ queryKey: ["tables"] });
      qc.invalidateQueries({ queryKey: ["table-overview"] });
      qc.invalidateQueries({ queryKey: ["payments"] });
      qc.invalidateQueries({ queryKey: ["fiscal-receipts"] });
    },
  });
}

// Admin rapor sayfası — günlük/tarih aralıklı Session listesi.
// Params: { status?, date_from?, date_to?, table? }
export function useTableSessions(params?: Record<string, any>) {
  return useQuery({
    queryKey: ["table-sessions", getTenantId(), params],
    queryFn: async () =>
      (await apiClient.get("/api/v1/table-sessions/", { params })).data,
  });
}

export function useFloorPlans() {
  return useQuery({
    queryKey: ["floor-plans", getTenantId()],
    queryFn: async () => (await apiClient.get("/api/v1/floor-plans/")).data,
  });
}

export function useReservations(filters?: Record<string, any>) {
  return useQuery({
    queryKey: ["reservations", getTenantId(), filters],
    queryFn: async () =>
      (await apiClient.get("/api/v1/reservations/", { params: filters })).data,
  });
}

// Rezervasyon güncelle (PATCH) — masa, kişi sayısı, tarih/saat, müşteri
// bilgileri ve not düzenlenebilir. status/kapora gibi alanlar serializer'da
// read-only; onlar deposit/seat/cancel aksiyonlarıyla değişir.
export function useUpdateReservation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Record<string, any> }) =>
      (await apiClient.patch(`/api/v1/reservations/${id}/`, patch)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reservations"] });
    },
  });
}

export function useTableStatusOverview(options?: { wsConnected?: boolean }) {
  // useTables ile aynı strateji: WS bağlıyken polling kapalı,
  // koparsa 30 sn fallback.
  const wsOk = options?.wsConnected ?? false;
  return useQuery({
    queryKey: ["table-overview", getTenantId()],
    queryFn: async () =>
      (await apiClient.get("/api/v1/reports/table-overview/")).data,
    refetchInterval: wsOk ? false : 30000,
  });
}

// ============================================================
// Inventory
// ============================================================
export function useStockItems() {
  return useQuery({
    queryKey: ["stock-items", getTenantId()],
    queryFn: async () => (await apiClient.get("/api/v1/stock-items/")).data,
  });
}

export function useStockAlerts() {
  return useQuery({
    queryKey: ["stock-alerts", getTenantId()],
    queryFn: async () => (await apiClient.get("/api/v1/stock-alerts/")).data,
    refetchInterval: 60000,
  });
}

// ============================================================
// BOM cost / margin
// ============================================================
export function useCostBreakdown(productId: string | null | undefined) {
  return useQuery({
    queryKey: ["cost-breakdown", getTenantId(), productId],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/products/${productId}/cost-breakdown/`)).data,
    enabled: !!productId,
  });
}

export function useMarginAlerts(maxMarginPercent = 30) {
  return useQuery({
    queryKey: ["margin-alerts", getTenantId(), maxMarginPercent],
    queryFn: async () =>
      (await apiClient.get(
        `/api/v1/products/margin-alerts/?max_margin_percent=${maxMarginPercent}`,
      )).data,
    refetchInterval: 5 * 60 * 1000,
  });
}

// ============================================================
// Consumption velocity + reorder suggestions (Sprint 32)
// ============================================================
export function useConsumptionSummary(days = 7) {
  return useQuery({
    queryKey: ["consumption-summary", getTenantId(), days],
    queryFn: async () =>
      (await apiClient.get(
        `/api/v1/inventory/consumption-summary/?days=${days}`,
      )).data,
  });
}

export function useReorderSuggestions(targetDays = 14, onlyLow = false) {
  return useQuery({
    queryKey: ["reorder-suggestions", getTenantId(), targetDays, onlyLow],
    queryFn: async () =>
      (await apiClient.get(
        `/api/v1/inventory/reorder-suggestions/?target_days=${targetDays}&only_low=${onlyLow ? "1" : "0"}`,
      )).data,
  });
}

export function useMonthlyPL(year: number, month: number) {
  return useQuery({
    queryKey: ["monthly-pl", getTenantId(), year, month],
    queryFn: async () =>
      (await apiClient.get(
        `/api/v1/reports/monthly-pl/?year=${year}&month=${month}`,
      )).data,
  });
}

// Esnek tarih aralığı Kâr/Zarar (N+1-siz backend). monthly-pl'in üst kümesi:
// keyfi [dateFrom, dateTo] aralığı + günlük breakdown. enabled ile tetiklenir.
export function usePLSummary(
  dateFrom: string,
  dateTo: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ["pl-summary", getTenantId(), dateFrom, dateTo],
    queryFn: async () =>
      (await apiClient.get(
        `/api/v1/reports/pl-summary/?date_from=${dateFrom}&date_to=${dateTo}`,
      )).data,
    enabled: options?.enabled ?? Boolean(dateFrom && dateTo),
  });
}

// CSV indirme — apiClient blob olarak çeker, tarayıcıda indirme tetikler.
export async function downloadPLSummaryCsv(
  dateFrom: string,
  dateTo: string,
): Promise<void> {
  const resp = await apiClient.get(
    `/api/v1/reports/pl-summary/?date_from=${dateFrom}&date_to=${dateTo}&export=csv`,
    { responseType: "blob" },
  );
  const url = window.URL.createObjectURL(new Blob([resp.data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = `kar-zarar_${dateFrom}_${dateTo}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// ============================================================
// Cashier closing flow (Sprint 33 — ADR-015)
// ============================================================
export function useCashierClosingState(options?: { enabled?: boolean }) {
  // Kasa kapanış banner'ı için. Henüz WS push'u yok (cashier.closing.* event'i
  // bridge'de tanımlı değil); polling 10→30 sn'ye çekildi — yükü 3 kat azaltır,
  // banner gecikmesi kabul edilebilir (kullanıcı zaten butona tıklayınca
  // mutation invalidate ediyor).
  return useQuery({
    queryKey: ["cashier-closing-state", getTenantId()],
    queryFn: async () =>
      (await apiClient.get("/api/v1/cashier/closing-state/")).data,
    refetchInterval: 30_000,
    enabled: options?.enabled ?? true,
    retry: false,
  });
}

export function useRequestZReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (await apiClient.post("/api/v1/cashier/request-z-report/")).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cashier-closing-state"] });
    },
  });
}

export function useCompleteZReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (await apiClient.post("/api/v1/cashier/complete-z-report/")).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cashier-closing-state"] });
      qc.invalidateQueries({ queryKey: ["daily-cashier"] });
    },
  });
}

export function useCancelZReportRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (await apiClient.post("/api/v1/cashier/cancel-z-report/")).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cashier-closing-state"] });
    },
  });
}

// ============================================================
// Genel Kasa (Safe) — iki-katmanlı kasa. Drawer (Shift) günlük çekmece;
// Safe büyük ödemeler + devreden nakit + drawer↔safe transferleri.
// ============================================================
export function useSafeMovements(branch?: string) {
  return useQuery({
    queryKey: ["cash-safe", getTenantId(), branch ?? null],
    queryFn: async () =>
      (await apiClient.get("/api/v1/cash-safe/movements/", {
        params: branch ? { branch } : {},
      })).data,
  });
}

/** Safe yazma işlemleri sonrası ilgili cache'leri tazeleyen ortak helper. */
function invalidateSafe(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["cash-safe"] });
  qc.invalidateQueries({ queryKey: ["cash-movements"] });
  qc.invalidateQueries({ queryKey: ["my-shift"] });
  qc.invalidateQueries({ queryKey: ["x-report"] });
}

/** Yeni idempotency anahtarı (çift-gönderim/retry koruması). Her kullanıcı
 *  işlemi başına bir kez üretilir; ağ retry'ında aynı kaldığı sürece backend
 *  ikinci kaydı yazmaz. crypto.randomUUID modern tarayıcılarda mevcut. */
function newIdemKey(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

export function useSafeDepositFromDrawer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { amount_minor: number; branch?: string; notes?: string }) =>
      (await apiClient.post("/api/v1/cash-safe/deposit-from-drawer/", vars, {
        headers: { "Idempotency-Key": newIdemKey() },
      })).data,
    onSuccess: () => invalidateSafe(qc),
  });
}

export function useSafeWithdrawToDrawer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { amount_minor: number; branch?: string; notes?: string }) =>
      (await apiClient.post("/api/v1/cash-safe/withdraw-to-drawer/", vars, {
        headers: { "Idempotency-Key": newIdemKey() },
      })).data,
    onSuccess: () => invalidateSafe(qc),
  });
}

export function useSafePay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      amount_minor: number;
      movement_type?: string;
      notes?: string;
      branch?: string;
    }) => (await apiClient.post("/api/v1/cash-safe/pay/", vars, {
      headers: { "Idempotency-Key": newIdemKey() },
    })).data,
    onSuccess: () => invalidateSafe(qc),
  });
}

// ============================================================
// RFQ (Sprint 34 — ADR-016) — Çoklu tedarikçi teklif
// ============================================================
export function useRFQs(filters?: Record<string, any>) {
  return useQuery({
    queryKey: ["rfqs", getTenantId(), filters],
    queryFn: async () =>
      (await apiClient.get("/api/v1/rfqs/", { params: filters })).data,
  });
}

export function useRFQ(id: string | null | undefined) {
  return useQuery({
    queryKey: ["rfq", getTenantId(), id],
    queryFn: async () => (await apiClient.get(`/api/v1/rfqs/${id}/`)).data,
    enabled: !!id,
  });
}

export function useRFQComparison(id: string | null | undefined) {
  return useQuery({
    queryKey: ["rfq-compare", getTenantId(), id],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/rfqs/${id}/compare/`)).data,
    enabled: !!id,
  });
}

export function useCreateRFQ() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { lines: any[]; supplier_ids: string[]; notes?: string }) =>
      (await apiClient.post("/api/v1/rfqs/", body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rfqs"] }),
  });
}

export function useSendRFQ() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await apiClient.post(`/api/v1/rfqs/${id}/send/`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rfqs"] });
      qc.invalidateQueries({ queryKey: ["rfq"] });
    },
  });
}

// RFQ oluşturmadan ÖNCE: seçilen kalemler için aday tedarikçi önerisi.
// item_refs: [{ingredient: id} | {supply_item: id}, ...]
// Dönüş: { suggestions: { "<kind>:<id>": [ {supplier_id, supplier_name,
//   is_preferred, source, last_price_minor}, ... ] } }
// "Hangi malı kimden alıyoruz" bilgisini RFQ'ya taşır; kategori + SupplierProduct'tan türetilir.
export interface SupplierSuggestion {
  supplier_id: string;
  supplier_name: string;
  is_preferred: boolean;
  source: "direct" | "category";
  last_price_minor: number | null;
}
export function useSuggestSuppliers() {
  return useMutation({
    mutationFn: async (item_refs: Array<{ ingredient?: string; supply_item?: string }>) =>
      (await apiClient.post("/api/v1/rfqs/suggest-suppliers/", { item_refs }))
        .data as { suggestions: Record<string, SupplierSuggestion[]> },
  });
}

// Bir tedarikçinin RFQ'da teklif verebileceği request_line id'leri (kategori +
// SupplierProduct). "Fiyat Gir" ekranı bununla filtreler — Et firmasına temizlik
// sorulmaz. Boş dönerse FE geri düşüşle tüm kalemleri gösterir.
export function useCoverableLines() {
  return useMutation({
    mutationFn: async ({ rfqId, supplierId }: { rfqId: string; supplierId: string }) =>
      (await apiClient.get(`/api/v1/rfqs/${rfqId}/coverable-lines/`, {
        params: { supplier: supplierId },
      })).data as { request_line_ids: string[] },
  });
}

// Bir tedarik kategorisinin kalemlerini çek (RFQ "Kategoriden Ekle" için).
// Kategori hem mutfak hammaddesi (ingredients) hem mutfak-dışı (supply_items) içerebilir.
export interface CategoryItem { id: string; name: string; unit: string }
export function useCategoryItems() {
  return useMutation({
    mutationFn: async (categoryId: string) =>
      (await apiClient.get(`/api/v1/procurement-categories/${categoryId}/items/`))
        .data as { ingredients: CategoryItem[]; supply_items: CategoryItem[] },
  });
}

export function useRecordQuoteResponse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      responseId, lines, leadTimeDays, notes,
    }: { responseId: string; lines: any[]; leadTimeDays?: number; notes?: string }) =>
      (await apiClient.post(`/api/v1/quote-responses/${responseId}/record/`, {
        lines, lead_time_days: leadTimeDays ?? 0, notes: notes ?? "",
      })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rfqs"] });
      qc.invalidateQueries({ queryKey: ["rfq"] });
      qc.invalidateQueries({ queryKey: ["rfq-compare"] });
    },
  });
}

export function useAcceptQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (responseId: string) =>
      (await apiClient.post(`/api/v1/quote-responses/${responseId}/accept/`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rfqs"] });
      qc.invalidateQueries({ queryKey: ["rfq"] });
      qc.invalidateQueries({ queryKey: ["purchase-orders"] });
    },
  });
}

/**
 * Kalem-bazlı kabul → her tedarikçi için ayrı PO.
 * line_awards: { [request_line_id]: response_id } — her ürün için seçilen
 * tedarikçinin teklif (response) id'si.
 */
export function useAcceptSplit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      rfqId, lineAwards,
    }: { rfqId: string; lineAwards: Record<string, string> }) =>
      (await apiClient.post(`/api/v1/rfqs/${rfqId}/accept-split/`, {
        line_awards: lineAwards,
      })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rfqs"] });
      qc.invalidateQueries({ queryKey: ["rfq"] });
      qc.invalidateQueries({ queryKey: ["purchase-orders"] });
    },
  });
}

export function useCancelRFQ() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) =>
      (await apiClient.post(`/api/v1/rfqs/${id}/cancel/`, { reason })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rfqs"] }),
  });
}

export function useSuppliers() {
  return useQuery({
    queryKey: ["suppliers", getTenantId()],
    queryFn: async () => (await apiClient.get("/api/v1/suppliers/")).data,
  });
}

export function useIngredients() {
  return useQuery({
    queryKey: ["ingredients", getTenantId()],
    queryFn: async () => (await apiClient.get("/api/v1/ingredients/")).data,
  });
}

// ============================================================
// Reports
// ============================================================
export function useDailyCashier() {
  return useQuery({
    queryKey: ["daily-cashier", getTenantId()],
    queryFn: async () =>
      (await apiClient.get("/api/v1/reports/daily-cashier/")).data,
    refetchInterval: 60000,
  });
}

export function useProductSales(params?: {
  date_from?: string | null;
  date_to?: string | null;
}) {
  const dateFrom = params?.date_from || null;
  const dateTo = params?.date_to || null;
  return useQuery({
    queryKey: ["product-sales", getTenantId(), dateFrom, dateTo],
    queryFn: async () => {
      const q: Record<string, string> = {};
      if (dateFrom) q.date_from = dateFrom;
      if (dateTo) q.date_to = dateTo;
      return (
        await apiClient.get("/api/v1/reports/product-sales/", { params: q })
      ).data;
    },
  });
}

export function useTableRevenue() {
  return useQuery({
    queryKey: ["table-revenue", getTenantId()],
    queryFn: async () =>
      (await apiClient.get("/api/v1/reports/table-revenue/")).data,
  });
}

// ============================================================
// KDS
// ============================================================
export function useKdsStations(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["kds-stations", getTenantId()],
    queryFn: async () => (await apiClient.get("/api/v1/kds-stations/")).data,
    enabled: options?.enabled ?? true,
    retry: false,
  });
}

export function useKdsTickets(
  filters?: Record<string, any>,
  options?: { enabled?: boolean; wsConnected?: boolean },
) {
  // KDS App'i /ws/kds/station/<code>/ + /ws/kds/ üzerinden ticket lifecycle
  // event'lerini canlı alıyor. WS bağlıyken polling kapalı; koparsa 30 sn
  // fallback (önceden her sekme her 5 sn'de bir liste çekiyordu — yoğun
  // saatte istasyon başına saatte 720 istek).
  const wsOk = options?.wsConnected ?? false;
  return useQuery({
    queryKey: ["kds-tickets", getTenantId(), filters],
    queryFn: async () =>
      (await apiClient.get("/api/v1/kds-tickets/", { params: filters })).data,
    refetchInterval: wsOk ? false : 30000,
    enabled: options?.enabled ?? true,
    retry: false,
  });
}

// ============================================================
// Mutations
// ============================================================

/**
 * Sipariş mutasyonu sonrası tazelenmesi gereken tüm cache'ler. POS açık
 * hesap görünümü `table-detail`'ten beslenir; masa rozetleri `tables` ve
 * `table-overview`'dan. Tek key invalidate edersek satır eklendikten sonra
 * sepette/açık hesapta gecikme olur.
 */
function invalidateOrderQueries(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["orders"] });
  qc.invalidateQueries({ queryKey: ["table-detail"] });
  qc.invalidateQueries({ queryKey: ["tables"] });
  qc.invalidateQueries({ queryKey: ["table-overview"] });
}

export function useCreateOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => (await apiClient.post("/api/v1/orders/", data)).data,
    onSuccess: () => invalidateOrderQueries(qc),
  });
}

export function useConfirmOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (orderId: string) =>
      (await apiClient.post(`/api/v1/orders/${orderId}/confirm/`)).data,
    onSuccess: () => invalidateOrderQueries(qc),
  });
}

export function useOpenShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { opening_cash_minor: number }) =>
      (await apiClient.post("/api/v1/shifts/", data)).data,
    // Kasa açılınca açık-kasa banner'ı, kasa defteri (OPENING_FLOAT hareketi)
    // ve "my_open" görünümleri hemen tazelensin.
    onSuccess: () => {
      ["shifts", "cash-movements", "my-shift"].forEach((key) =>
        qc.invalidateQueries({ queryKey: [key] }));
    },
  });
}

export function useMyOpenShift(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["shifts", "my_open"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/shifts/my_open/")).data as {
        shift: { id: string; is_open: boolean } | null;
      },
    enabled: opts?.enabled ?? true,
    staleTime: 30_000,
  });
}

export function useTransitionTable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ tableId, state }: { tableId: string; state: string }) =>
      (await apiClient.patch(`/api/v1/tables/${tableId}/state/`, { state })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tables"] });
      qc.invalidateQueries({ queryKey: ["floor-plans"] });
      qc.invalidateQueries({ queryKey: ["table-overview"] });
    },
  });
}

// Masa kısmi güncelleme — örn. assigned_waiter (ilgili garson) ataması.
export function useUpdateTable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ tableId, patch }: { tableId: string; patch: Record<string, any> }) =>
      (await apiClient.patch(`/api/v1/tables/${tableId}/`, patch)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tables"] });
      qc.invalidateQueries({ queryKey: ["table-detail"] });
    },
  });
}

// Masalar tablosunda sürükle-bırak sıralama — yeni id sırası gönderilir,
// backend her masanın sort_order'ını 0,1,2,… olarak yeniden numaralandırır.
export function useReorderTables() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (order: string[]) =>
      (await apiClient.post(`/api/v1/tables/reorder/`, { order })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tables"] });
    },
  });
}

// Garson seçim listesi — masaya ilgili garson atama Select'i için.
// WaiterProfile.user (auth.User id) → Table.assigned_waiter ile eşleşir.
export function useWaiterOptions() {
  return useQuery({
    queryKey: ["waiter-options", getTenantId()],
    queryFn: async () => {
      const data = (await apiClient.get("/api/v1/waiter-profiles/")).data;
      const rows: any[] = data?.results || data || [];
      return rows
        .filter((w) => w.is_active && w.user)
        .map((w) => ({
          value: String(w.user),
          label: w.display_name || w.nickname || w.username || "Garson",
        }));
    },
    staleTime: 60_000,
  });
}

// ============================================================
// Dashboard
// ============================================================
export function useDashboardSummary() {
  return useQuery({
    queryKey: ["dashboard-summary", getTenantId()],
    queryFn: async () =>
      (await apiClient.get("/api/v1/dashboard/summary/")).data,
    refetchInterval: 60000,
  });
}

// ============================================================
// POS Mutations — Order workflow
// ============================================================

export function useAddOrderLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ orderId, ...payload }: {
      orderId: string;
      product_id: string;
      quantity: number;
      modifier_ids?: string[];
      notes?: string;
      excluded_ingredient_ids?: string[];
    }) =>
      (await apiClient.post(`/api/v1/orders/${orderId}/add_line/`, payload)).data,
    onSuccess: () => invalidateOrderQueries(qc),
  });
}

export function useRemoveOrderLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ orderId, lineId }: { orderId: string; lineId: string }) =>
      (await apiClient.post(`/api/v1/orders/${orderId}/remove_line/${lineId}/`)).data,
    onSuccess: () => invalidateOrderQueries(qc),
  });
}

export function useTenderOrder() {
  const qc = useQueryClient();
  return useMutation({
    // paid_line_ids: opsiyonel; verilirse SADECE o OrderLine'lar ödenir
    // (satır seçimli parçalı ödeme). Verilmezse mevcut "tüm sipariş"
    // davranışı (tek/eşit parçalı).
    // line_paid_quantities: {lineId: qty} — çok porsiyonlu satırda kısmi
    // adet öde ("3× Antrikot'un 1'ini öde"). Verilmezse satırın tüm kalanı.
    mutationFn: async ({
      orderId, tenders, paid_line_ids, line_paid_quantities,
    }: {
      orderId: string;
      tenders: Array<{ type: string; amount_minor: number }>;
      paid_line_ids?: string[];
      line_paid_quantities?: Record<string, number>;
    }) =>
      (await apiClient.post(`/api/v1/orders/${orderId}/tender/`, {
        tenders,
        ...(paid_line_ids?.length ? { paid_line_ids } : {}),
        ...(line_paid_quantities && Object.keys(line_paid_quantities).length
          ? { line_paid_quantities }
          : {}),
      })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["tables"] });
      qc.invalidateQueries({ queryKey: ["table-detail"] });
      qc.invalidateQueries({ queryKey: ["table-overview"] });
      qc.invalidateQueries({ queryKey: ["daily-cashier"] });
    },
  });
}

// Hesap aktarımı (Bölüm 3) — satır seçimli, masalar arası.
// Source siparişten seçili lines target masaya taşınır; target'ta açık
// Order varsa oraya eklenir, yoksa yeni açılır + CONFIRMED.
export function useTransferLines() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orderId, targetTableId, lineIds, lineQuantities,
    }: {
      orderId: string;
      targetTableId: string;
      lineIds: string[];
      // {lineId: aktarılacak qty} — kısmi aktarım; verilmezse satırın tamamı
      lineQuantities?: Record<string, number>;
    }) =>
      (await apiClient.post(`/api/v1/orders/${orderId}/transfer-lines/`, {
        target_table_id: targetTableId,
        line_ids: lineIds,
        ...(lineQuantities ? { line_quantities: lineQuantities } : {}),
      })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["tables"] });
      qc.invalidateQueries({ queryKey: ["table-detail"] });
      qc.invalidateQueries({ queryKey: ["table-overview"] });
    },
  });
}

// Adisyon (masa) seviyesinde transfer — birden çok siparişten seçili kalemleri
// hedef masada TEK siparişte birleştirir. line_ids herhangi bir açık siparişten
// gelebilir; backend kaynak siparişe göre gruplar.
export function useTransferSessionLines() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      sourceTableId, targetTableId, lineIds, lineQuantities,
    }: {
      sourceTableId: string;
      targetTableId: string;
      lineIds: string[];
      lineQuantities?: Record<string, number>;
    }) =>
      (await apiClient.post(
        `/api/v1/tables/${sourceTableId}/transfer-session-lines/`,
        {
          target_table_id: targetTableId,
          line_ids: lineIds,
          ...(lineQuantities ? { line_quantities: lineQuantities } : {}),
        },
      )).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["tables"] });
      qc.invalidateQueries({ queryKey: ["table-detail"] });
      qc.invalidateQueries({ queryKey: ["table-overview"] });
    },
  });
}

export function useCancelOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ orderId, reason }: { orderId: string; reason?: string }) =>
      (await apiClient.post(`/api/v1/orders/${orderId}/cancel/`, { reason })).data,
    onSuccess: () => invalidateOrderQueries(qc),
  });
}

// ============================================================
// QR pending — Garson POS app
// ============================================================
// Mutfak hazır siparişler — garson POS "Hazır" sekmesi listeler.
// WS bağlıyken polling kapalı; WS koparsa 30 sn fallback (hazır listesi
// gecikmemeli — garson masaya geç gitmesin).
export function useReadyKdsTickets(opts?: { enabled?: boolean; wsConnected?: boolean }) {
  const wsOk = opts?.wsConnected ?? false;
  return useQuery({
    queryKey: ["kds-tickets", "ready", getTenantId()],
    queryFn: async () =>
      (await apiClient.get("/api/v1/kds-tickets/?status=READY")).data,
    enabled: opts?.enabled ?? true,
    refetchInterval: wsOk ? false : 30000,
  });
}

export function useMarkTicketServed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ticketId: string) =>
      (await apiClient.post(`/api/v1/kds-tickets/${ticketId}/served/`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["kds-tickets"] });
      qc.invalidateQueries({ queryKey: ["tables"] });
    },
  });
}

export function useQrPendingOrders(opts?: { enabled?: boolean; wsConnected?: boolean }) {
  // QrApproval sayfası /ws/waiters/ üzerinden qr.order.pending_approval
  // ve order.confirmed event'lerinde bu query'yi invalidate ediyor. WS
  // bağlıyken polling kapalı; koparsa 60 sn fallback (10 sn aşırı sıktı).
  const wsOk = opts?.wsConnected ?? false;
  return useQuery({
    queryKey: ["qr-pending", getTenantId()],
    queryFn: async () => (await apiClient.get("/api/v1/orders/qr-pending/")).data,
    // Login öncesi çağrılmamalı — token yokken 401 + reload döngüsü olur.
    enabled: opts?.enabled ?? true,
    refetchInterval: wsOk ? false : 60000,
  });
}

export function useApproveQrOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (orderId: string) =>
      (await apiClient.post(`/api/v1/orders/${orderId}/qr-approve/`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["qr-pending"] });
      qc.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}

export function useRejectQrOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ orderId, reason }: { orderId: string; reason?: string }) =>
      (await apiClient.post(`/api/v1/orders/${orderId}/qr-reject/`, { reason })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["qr-pending"] }),
  });
}

// ============================================================
// PendingQrRequest — Yeni QR Onay mimarisi.
// Müşteri /qr/requests/ ile request kaydeder (Order yaratılmaz). Garson
// approve edince stok kontrolü yapılır + CONFIRMED Order yaratılır.
// Stok yetersizse 400 + shortages → frontend pop-up, garson is_optional
// malzemeleri çıkarıp retry yapar (extra_excluded_ingredient_ids).
// ============================================================
export function useQrPendingRequests(
  opts?: { enabled?: boolean; wsConnected?: boolean },
) {
  const wsOk = opts?.wsConnected ?? false;
  return useQuery({
    queryKey: ["qr-requests-pending", getTenantId()],
    queryFn: async () => (await apiClient.get("/api/v1/qr-requests/")).data,
    enabled: opts?.enabled ?? true,
    refetchInterval: wsOk ? false : 60000,
  });
}

export function useApproveQrRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      requestId: string;
      extra_excluded_ingredient_ids?: Record<string, string[]>;
      // Garson masada miktar düzeltmesi: {satır_idx: yeni_miktar}, 0 → çıkar.
      line_overrides?: Record<string, number>;
    }) => {
      const { requestId, ...body } = vars;
      const { data } = await apiClient.post(
        `/api/v1/qr-requests/${requestId}/approve/`,
        body,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["qr-requests-pending"] });
      qc.invalidateQueries({ queryKey: ["qr-pending"] });  // legacy
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["tables"] });
      qc.invalidateQueries({ queryKey: ["table-detail"] });
    },
  });
}

export function useRejectQrRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ requestId, reason }: { requestId: string; reason?: string }) =>
      (await apiClient.post(
        `/api/v1/qr-requests/${requestId}/reject/`,
        { reason },
      )).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["qr-requests-pending"] });
      qc.invalidateQueries({ queryKey: ["tables"] });
    },
  });
}

// ============================================================
// KDS Mutations
// ============================================================
export function useStartTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ticketId: string) =>
      (await apiClient.post(`/api/v1/kds-tickets/${ticketId}/start/`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["kds-tickets"] }),
  });
}

// Aşçı "yaktım" → satırın BOM'u WASTED düşer + opsiyonel remake (HIGH-priority
// yeni ticket). Backend waste_kds_line.
export function useWasteKdsLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      ticketId: string;
      line_id: string;
      reason_code: string;
      notes?: string;
      remake?: boolean;
      // Dolu ise bileşen-bazlı (partial) fire — sadece bu malzeme(ler) zayi.
      // Gönderilmezse/boşsa tüm tabak fire (klasik akış).
      wasted_ingredient_ids?: string[];
    }) => (await apiClient.post(
      `/api/v1/kds-tickets/${payload.ticketId}/waste-line/`,
      {
        line_id: payload.line_id,
        reason_code: payload.reason_code,
        notes: payload.notes,
        remake: payload.remake ?? true,
        ...(payload.wasted_ingredient_ids?.length
          ? { wasted_ingredient_ids: payload.wasted_ingredient_ids }
          : {}),
      },
    )).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["kds-tickets"] });
      qc.invalidateQueries({ queryKey: ["stock-movements"] });
    },
  });
}

export function useMarkReady() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ticketId: string) =>
      (await apiClient.post(`/api/v1/kds-tickets/${ticketId}/ready/`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["kds-tickets"] }),
  });
}

export function useMarkServed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ticketId: string) =>
      (await apiClient.post(`/api/v1/kds-tickets/${ticketId}/served/`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["kds-tickets"] }),
  });
}

// ============================================================
// Shift / Cash register
// ============================================================
export function useMyShift() {
  return useQuery({
    queryKey: ["my-shift", getTenantId()],
    queryFn: async () => {
      const { data } = await apiClient.get("/api/v1/shifts/", {
        params: { closed_at__isnull: true },
      });
      return data.results?.[0] || data[0] || null;
    },
  });
}

/** Kapanış öncesi X raporu: beklenen nakit + kalem dökümü (opening/sales/...). */
export function useShiftXReport(shiftId: string | null | undefined) {
  return useQuery({
    queryKey: ["shift-x-report", getTenantId(), shiftId],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/shifts/${shiftId}/x_report/`)).data,
    enabled: !!shiftId,
    staleTime: 0, // her açılışta taze çek (sayım anındaki gerçek durum)
  });
}

export function useCloseShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ shiftId, counted_cash_minor, notes }: { shiftId: string; counted_cash_minor: number; notes?: string }) =>
      (await apiClient.post(`/api/v1/shifts/${shiftId}/close/`, { counted_cash_minor, notes })).data,
    // Kapanış sonrası tüm nakit/kasa/dashboard görünümleri stale kalmasın.
    onSuccess: () => {
      ["my-shift", "shifts", "cash-movements", "shift-x-report",
       "dashboard-summary", "daily-cashier"].forEach((key) =>
        qc.invalidateQueries({ queryKey: [key] }),
      );
    },
  });
}

// ============================================================
// QR Public (no auth) — Menu app
// ============================================================
export function useResolveQrToken(token: string | null) {
  return useQuery({
    queryKey: ["qr-resolve", token],
    queryFn: async () => (await apiClient.get(`/qr/resolve/${token}/`)).data,
    enabled: !!token,
    retry: false,
  });
}

export function usePublicMenu(token: string | null, lang: string = "tr") {
  return useQuery({
    // lang queryKey'de → dil değişince payload (çevrilmiş ad/içindekiler/
    // alerjen) yeniden çekilir. Backend ?lang ile çevrilmiş menü döner.
    queryKey: ["qr-menu", token, lang],
    queryFn: async () =>
      (await apiClient.get(`/qr/menu/${token}/`, { params: { lang } })).data,
    enabled: !!token,
    // Dil değişirken yeni dil gelene kadar önceki menü ekranda kalır —
    // yükleme ekranına düşüp seçili kategori/ürün listesi kaybolmaz.
    placeholderData: keepPreviousData,
  });
}

// QR müşteri sipariş gönderme — Yeni mimari kullanır (/qr/requests/).
// Order YARATILMAZ; PendingQrRequest kaydedilir. Garson POS QR Onay'da
// approve edince Order CONFIRMED yaratılır. Stok kontrolü garson onayında
// — müşteri stoktan bilemez, asla reddedilmez.
//
// Geriye uyumluluk: response yapısı eskiyle benzer ama "order_id" yerine
// "request_id" döner. menu/App.tsx bunu dikkate alır; "kitchen_status"
// alanı yoksa müşteriye "garson onayı bekleniyor" mesajı gösterilir.
export function useCreateQrOrder() {
  return useMutation({
    mutationFn: async (payload: {
      qr_token: string;
      lines: Array<{
        product_id: string;
        quantity: number;
        modifier_ids?: string[];
        excluded_ingredient_ids?: string[];
        notes?: string;
      }>;
      customer_name?: string;
      customer_phone?: string;
      notes?: string;
    }) => {
      // Müşteri giriş yapmışsa customer_token'ı header'a ekle → backend
      // siparişi bu müşteriye bağlar (loyalty puanı). apiClient interceptor'ı
      // sadece staff auth_token'ı koyar; QR müşterisi customer_token tutar,
      // bu yüzden header'ı burada açıkça geçiriyoruz. Token yoksa anonim.
      const customerToken =
        typeof localStorage !== "undefined"
          ? localStorage.getItem("customer_token")
          : null;
      const config = customerToken
        ? { headers: { Authorization: `Bearer ${customerToken}` } }
        : undefined;
      return (await apiClient.post(`/qr/requests/`, payload, config)).data;
    },
  });
}

// QR müşteri bekleyen isteğini iptal eder (garson onayından önce vazgeçti).
// POST /qr/requests/{id}/cancel/ — qr_token ile masa doğrulanır. Garson zaten
// onayladıysa 409 döner (Order yaratılmış, iptal edilemez). Başarıda takip
// listesi tazelenir → "Garson onayı bekleniyor" kartı kaybolur.
export function useCancelQrRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      requestId, qr_token, reason,
    }: { requestId: string; qr_token: string; reason?: string }) =>
      (await apiClient.post(
        `/qr/requests/${requestId}/cancel/`,
        { qr_token, reason },
      )).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["qr-table-orders"] });
    },
  });
}

// QR masa sipariş takibi — canlı durum WebSocket ile gelir (polling yok).
// Ekran ilk açılışta + WS her bağlandığında bu query invalidate edilir.
export function useTableOrders(token: string | null) {
  return useQuery({
    queryKey: ["qr-table-orders", token],
    queryFn: async () => (await apiClient.get(`/qr/orders/${token}/`)).data,
    enabled: !!token,
  });
}

// QR hesap/ödeme isteği — garson çağrılır (tek veya parçalı)
export function useRequestQrPayment() {
  return useMutation({
    mutationFn: async (payload: {
      qr_token: string;
      order_id?: string;
      split?: { mode: "single" | "split"; parts: number };
    }) => (await apiClient.post(`/qr/payment/`, payload)).data,
  });
}

// ============================================================
// Catalog / Inventory misc
// ============================================================
export function useModifiers() {
  return useQuery({
    queryKey: ["modifiers", getTenantId()],
    queryFn: async () => (await apiClient.get("/api/v1/modifiers/")).data,
  });
}

// Modifier'lar tablosunda sürükle-bırak sıralama.
export function useReorderModifiers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (order: string[]) =>
      (await apiClient.post(`/api/v1/modifiers/reorder/`, { order })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["modifiers"] });
    },
  });
}

export function useStockMovements(filters?: Record<string, any>) {
  return useQuery({
    queryKey: ["stock-movements", getTenantId(), filters],
    queryFn: async () =>
      (await apiClient.get("/api/v1/stock-movements/", { params: filters })).data,
  });
}

// ============================================================
// Stok düzeltme onay akışı (Sprint 36 — ADR-019)
// ============================================================
export function useAdjustmentRequests(filters?: Record<string, any>) {
  return useQuery({
    queryKey: ["adjustment-requests", getTenantId(), filters],
    queryFn: async () =>
      (await apiClient.get("/api/v1/adjustment-requests/", { params: filters })).data,
    refetchInterval: 30_000,
  });
}

export function useApproveAdjustment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await apiClient.post(`/api/v1/adjustment-requests/${id}/approve/`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["adjustment-requests"] });
      qc.invalidateQueries({ queryKey: ["stock-items"] });
      qc.invalidateQueries({ queryKey: ["stock-movements"] });
    },
  });
}

export function useRejectAdjustment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) =>
      (await apiClient.post(`/api/v1/adjustment-requests/${id}/reject/`, { reason })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["adjustment-requests"] });
    },
  });
}

// ============================================================
// Comp (ikram) — müşteri beğenmedi → müdür onaylı bedelden düşme
// ============================================================
export function useLineComps(filters?: Record<string, any>) {
  return useQuery({
    queryKey: ["line-comps", getTenantId(), filters],
    queryFn: async () =>
      (await apiClient.get("/api/v1/line-comps/", { params: filters })).data,
    refetchInterval: 30_000,
  });
}

export function useRequestLineComp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      order_line_id: string;
      comp_quantity: number;
      reason_code: string;
      notes?: string;
      remake?: boolean;
    }) => (await apiClient.post("/api/v1/line-comps/", payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["line-comps"] });
    },
  });
}

export function useApproveLineComp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await apiClient.post(`/api/v1/line-comps/${id}/approve/`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["line-comps"] });
      qc.invalidateQueries({ queryKey: ["stock-movements"] });
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["daily-cashier"] });
    },
  });
}

export function useRejectLineComp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) =>
      (await apiClient.post(`/api/v1/line-comps/${id}/reject/`, { reason })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["line-comps"] });
    },
  });
}

// Taslak satır ikramı — TASLAK/OPEN siparişte satırı %100 indirimle ücretsiz
// yapar (sektör-doğru "direkt ikram"; gerçek comp CONFIRMED+ için).
export function useCompDraftLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ orderId, lineId, reason }: {
      orderId: string; lineId: string; reason?: string;
    }) =>
      (await apiClient.post(
        `/api/v1/orders/${orderId}/comp-draft-line/${lineId}/`,
        reason ? { reason } : {},
      )).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["table-detail"] });
    },
  });
}

// ============================================================
// Ürün değişimi (exchange) — eski satır iade + yeni ürün ekle
// ============================================================
export function useExchangeLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      orderId: string;
      order_line_id: string;
      new_product_id: string;
      new_quantity?: number;
      opened: boolean;
      reason_code: string;
      return_quantity?: number;
      notes?: string;
    }) => {
      const { orderId, ...body } = vars;
      return (await apiClient.post(
        `/api/v1/orders/${orderId}/exchange-line/`, body,
      )).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["table-detail"] });
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["line-returns"] });
    },
  });
}

export function useLineReturns(filters?: Record<string, any>) {
  return useQuery({
    queryKey: ["line-returns", getTenantId(), filters],
    queryFn: async () =>
      (await apiClient.get("/api/v1/line-returns/", { params: filters })).data,
    refetchInterval: 30_000,
  });
}

export function useApproveLineReturn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await apiClient.post(`/api/v1/line-returns/${id}/approve/`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["line-returns"] });
      qc.invalidateQueries({ queryKey: ["stock-movements"] });
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["daily-cashier"] });
    },
  });
}

export function useRejectLineReturn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) =>
      (await apiClient.post(`/api/v1/line-returns/${id}/reject/`, { reason })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["line-returns"] });
    },
  });
}

// ============================================================
// Tedarikçi cari hesap (Sprint 37)
// ============================================================
export function useSupplierLedger(supplierId: string | null | undefined) {
  return useQuery({
    queryKey: ["supplier-ledger", getTenantId(), supplierId],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/suppliers/${supplierId}/ledger/`)).data,
    enabled: !!supplierId,
  });
}

export function useSupplierBalance(supplierId: string | null | undefined) {
  return useQuery({
    queryKey: ["supplier-balance", getTenantId(), supplierId],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/suppliers/${supplierId}/balance/`)).data,
    enabled: !!supplierId,
  });
}

export function useSupplierAdjustment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      supplierId, amount_minor, description, entry_date,
    }: { supplierId: string; amount_minor: number; description: string; entry_date?: string }) =>
      (await apiClient.post(
        `/api/v1/suppliers/${supplierId}/adjustment/`,
        { amount_minor, description, ...(entry_date && { entry_date }) },
      )).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["supplier-ledger", getTenantId(), vars.supplierId] });
      qc.invalidateQueries({ queryKey: ["supplier-balance", getTenantId(), vars.supplierId] });
      qc.invalidateQueries({ queryKey: ["suppliers"] });
    },
  });
}

// Yanlış girilen manuel hareketi (ADJUSTMENT) ters kayıtla iptal et.
export function useReverseAdjustment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ supplierId, entryId }: { supplierId: string; entryId: string }) =>
      (await apiClient.post(
        `/api/v1/suppliers/${supplierId}/reverse-adjustment/`,
        { entry_id: entryId },
      )).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["supplier-ledger", getTenantId(), vars.supplierId] });
      qc.invalidateQueries({ queryKey: ["supplier-balance", getTenantId(), vars.supplierId] });
      qc.invalidateQueries({ queryKey: ["suppliers"] });
    },
  });
}

export function useSupplierOpeningBalance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      supplierId, amount_minor, entry_date,
    }: { supplierId: string; amount_minor: number; entry_date?: string }) =>
      (await apiClient.post(
        `/api/v1/suppliers/${supplierId}/opening-balance/`,
        { amount_minor, ...(entry_date && { entry_date }) },
      )).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["supplier-ledger", getTenantId(), vars.supplierId] });
      qc.invalidateQueries({ queryKey: ["supplier-balance", getTenantId(), vars.supplierId] });
      qc.invalidateQueries({ queryKey: ["suppliers"] });
    },
  });
}

export function useSupplierAging() {
  return useQuery({
    queryKey: ["supplier-aging", getTenantId()],
    queryFn: async () => (await apiClient.get(`/api/v1/suppliers/aging/`)).data,
  });
}

export function useInstruments(filters?: Record<string, any>) {
  return useQuery({
    queryKey: ["instruments", getTenantId(), filters],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/instruments/`, { params: filters })).data,
  });
}

export function useCreateInstrument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      instrument_type: "CHECK" | "PROMISSORY_NOTE";
      supplier: string;
      serial_no: string;
      issue_date: string;
      due_date: string;
      amount_minor: number;
      bank_name?: string;
      bank_branch?: string;
      drawer_name?: string;
      issue_place?: string;
      currency?: string;
      notes?: string;
    }) => (await apiClient.post(`/api/v1/instruments/`, body)).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["instruments"] });
      qc.invalidateQueries({ queryKey: ["supplier-ledger", getTenantId(), vars.supplier] });
      qc.invalidateQueries({ queryKey: ["supplier-balance", getTenantId(), vars.supplier] });
      qc.invalidateQueries({ queryKey: ["suppliers"] });
    },
  });
}

/** Çek/senet fotosu yükle (çek fotosu veya elden teslim fotosu). */
export function useUploadInstrumentImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id, file, kind,
    }: { id: string; file: File; kind: "CHEQUE_PHOTO" | "HANDOVER_PHOTO" }) => {
      const fd = new FormData();
      fd.append("image", file);
      fd.append("kind", kind);
      return (await apiClient.post(`/api/v1/instruments/${id}/upload-image/`, fd)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["instruments"] });
    },
  });
}

export function useChangeInstrumentStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) =>
      (await apiClient.post(`/api/v1/instruments/${id}/change-status/`, { status })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["instruments"] });
      qc.invalidateQueries({ queryKey: ["supplier-ledger"] });
      qc.invalidateQueries({ queryKey: ["supplier-balance"] });
      qc.invalidateQueries({ queryKey: ["suppliers"] });
    },
  });
}

// Faturasız (cari hesaba / on-account) tedarikçi ödemesi.
export function useRecordSupplierPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      supplierId, amount_minor, payment_method, paid_at, reference, notes,
    }: {
      supplierId: string;
      amount_minor: number;
      payment_method: string;
      paid_at?: string;
      reference?: string;
      notes?: string;
    }) =>
      (await apiClient.post(`/api/v1/suppliers/${supplierId}/payment/`, {
        amount_minor,
        payment_method,
        ...(paid_at && { paid_at }),
        ...(reference && { reference }),
        ...(notes && { notes }),
      })).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["supplier-ledger", getTenantId(), vars.supplierId] });
      qc.invalidateQueries({ queryKey: ["supplier-balance", getTenantId(), vars.supplierId] });
      qc.invalidateQueries({ queryKey: ["suppliers"] });
    },
  });
}

// ============================================================
// PO → Mal Girişi köprüsü (receivable-lines / receive-all / receipt-history / close)
// ============================================================
export interface ReceivableLine {
  po_line_id: string;
  ingredient_id: string;
  ingredient_name: string;
  unit: string;
  quantity_remaining: string;
  unit_price_minor: number;
}

export function useReceivableLines(poId: string | null | undefined) {
  return useQuery({
    queryKey: ["po-receivable-lines", getTenantId(), poId],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/purchase-orders/${poId}/receivable-lines/`)).data,
    enabled: !!poId,
  });
}

export function useReceiptHistory(poId: string | null | undefined) {
  return useQuery({
    queryKey: ["po-receipt-history", getTenantId(), poId],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/purchase-orders/${poId}/receipt-history/`)).data,
    enabled: !!poId,
  });
}

function invalidatePoRelated(qc: ReturnType<typeof useQueryClient>, poId?: string) {
  qc.invalidateQueries({ queryKey: ["purchase-orders"] });
  qc.invalidateQueries({ queryKey: ["goods-receipts"] });
  qc.invalidateQueries({ queryKey: ["supplier-ledger"] });
  qc.invalidateQueries({ queryKey: ["supplier-balance"] });
  qc.invalidateQueries({ queryKey: ["suppliers"] });
  if (poId) {
    qc.invalidateQueries({ queryKey: ["po-receivable-lines", getTenantId(), poId] });
    qc.invalidateQueries({ queryKey: ["po-receipt-history", getTenantId(), poId] });
  }
}

// Tam kabul kısayolu: kalan tüm kalemleri PO fiyatıyla tek adımda teslim al.
export function useReceiveAllPO() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      poId, location, delivery_note_number,
    }: { poId: string; location: string; delivery_note_number?: string }) =>
      (await apiClient.post(`/api/v1/purchase-orders/${poId}/receive-all/`, {
        location, delivery_note_number,
      })).data,
    onSuccess: (_, vars) => invalidatePoRelated(qc, vars.poId),
  });
}

// Eksik kalem → PO'yu kapat (kalanı iptal).
export function useClosePO() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ poId, reason }: { poId: string; reason?: string }) =>
      (await apiClient.post(`/api/v1/purchase-orders/${poId}/close/`, {
        ...(reason && { reason }),
      })).data,
    onSuccess: (_, vars) => invalidatePoRelated(qc, vars.poId),
  });
}

// PO satırının KDV oranını ayarla → PO net/KDV/brüt toplamları yenilenir.
// KDV yalnız gösterim; stok/cariye sızmaz.
export function useSetPoLineTax() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      poId, poLineId, taxRatePercent,
    }: { poId: string; poLineId: string; taxRatePercent: number }) =>
      (await apiClient.post(`/api/v1/purchase-orders/${poId}/set-line-tax/`, {
        po_line_id: poLineId,
        tax_rate_percent: taxRatePercent,
      })).data,
    onSuccess: (_, vars) => invalidatePoRelated(qc, vars.poId),
  });
}
